// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ReviewPreflight.test.tsx
import { Checkbox, Text } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import { QuestionFormCard } from "./QuestionFormCard";
import {
  REVIEW_PREFLIGHT_GROUPS,
  type ReviewPreflightRow,
  reviewPreflightRowCopy,
} from "./reviewPreflightCopy";

import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type ReviewPreflightCategory = ReviewPreflightRow & {
  /** Nested under a parent row (Figma scope: Nutrition / Product / Region). */
  indent?: boolean;
};

export type ReviewPreflightProps = {
  variant?: "confirm" | "scope";
  categories: ReviewPreflightCategory[];
  selectedCategoryIds?: string[];
  onToggleCategory?: (id: string) => void;
  onStart: () => void;
  onChangeScope: () => void;
  onCancel?: () => void;
  summary?: string;
  description?: string;
  /** The always-offered Other row; the focus field shows while it is checked. */
  otherChecked?: boolean;
  onToggleOther?: () => void;
  focusValue?: string;
  onFocusChange?: (value: string) => void;
  testIdPrefix?: string;
  /** Gray out controls after Start or while a send is in flight. */
  disabled?: boolean;
  /** Persisted submit — keeps Start and checkboxes disabled after refresh. */
  submitted?: boolean;
  /** False on history turns; checkboxes and Start ignore input when false. */
  interactive?: boolean;
};

const PANEL_CLASS =
  "flex flex-col gap-2 rounded-xl border border-puntt-neutral-gray-4 bg-puntt-neutral-gray-2 p-2";

/**
 * Pre-review confirm / scope picker. Customer-facing scope copy only — no fake
 * progress chrome (Figma ReviewPreflight 49613:704).
 */
export function ReviewPreflight({
  variant = "confirm",
  categories,
  selectedCategoryIds = [],
  onToggleCategory,
  onStart,
  onChangeScope,
  onCancel,
  summary,
  description,
  otherChecked = false,
  onToggleOther,
  focusValue,
  onFocusChange,
  testIdPrefix = "review-preflight",
  disabled = false,
  submitted = false,
  interactive = true,
}: ReviewPreflightProps) {
  const { t } = useTranslation("ui");
  const selected = categories.filter((c) => selectedCategoryIds.includes(c.id));
  const confirmTitle =
    summary ??
    (selected.map((c) => reviewPreflightRowCopy(c, t).name).join(", ") ||
      t("reviewPreflight.title"));
  const confirmDescription =
    description ?? t("reviewPreflight.confirmDescription");
  const scopeTitle = summary ?? t("reviewPreflight.scopeTitle");
  // A form with no standard row is an artwork-only review. Its line is a
  // locale string, keyed by that shape, not by a language field.
  const artworkOnly =
    categories.length === 0 ||
    (categories.every((category) => category.group != null) &&
      categories.every((category) => category.group !== "standard"));
  const scopeDescription =
    description ??
    (artworkOnly
      ? t("reviewPreflight.artworkOnlyDescription")
      : t("reviewPreflight.scopeDescription"));
  const isScope = variant === "scope";
  const controlsLocked = !interactive || disabled || submitted;
  // why: zero rows is an artwork with no spec, so Start still runs the text
  // checks. A form that has rows stays locked until one is checked.
  const startLocked =
    controlsLocked ||
    (categories.length > 0 && selectedCategoryIds.length === 0);
  const grouped = categories.some((category) => category.group != null);
  const renderRow = (category: ReviewPreflightCategory) => {
    const checked = selectedCategoryIds.includes(category.id);
    const copy = reviewPreflightRowCopy(category, t);
    return (
      <label
        key={category.id}
        data-testid={`${testIdPrefix}-row-${category.id}`}
        aria-label={copy.name}
        className={
          category.indent
            ? "flex cursor-pointer items-start gap-2 pl-6"
            : "flex cursor-pointer items-start gap-2"
        }
      >
        <Checkbox
          checked={checked}
          disabled={controlsLocked}
          onCheckedChange={() => {
            if (!controlsLocked) onToggleCategory?.(category.id);
          }}
          aria-label={copy.name}
          size="2"
        />
        <span className="flex min-w-0 flex-col">
          <Text size="2" className="text-puntt-neutral-gray-12">
            {copy.name}
          </Text>
          {copy.description ? (
            <Text size="1" className="text-puntt-neutral-gray-11">
              {copy.description}
            </Text>
          ) : null}
        </span>
      </label>
    );
  };

  return (
    <div
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.reviewPreflight)}
      data-testid={testIdPrefix}
      className={PANEL_CLASS}
    >
      <QuestionFormCard
        chrome="inner"
        testId={`${testIdPrefix}-form`}
        testIdPrefix={testIdPrefix}
        detail1={isScope ? scopeTitle : confirmTitle}
        detail2={isScope ? scopeDescription : confirmDescription}
        primary={{
          action: "start",
          testId: `${testIdPrefix}-start`,
          label: t("reviewPreflight.startReview"),
          onClick: onStart,
          disabled: startLocked,
        }}
        tertiary={
          isScope
            ? onCancel
              ? {
                  action: "cancel",
                  testId: `${testIdPrefix}-cancel`,
                  label: t("reviewPreflight.cancel"),
                  onClick: onCancel,
                  disabled: controlsLocked,
                }
              : undefined
            : {
                action: "change-scope",
                testId: `${testIdPrefix}-change-scope`,
                label: t("reviewPreflight.changeScope"),
                onClick: onChangeScope,
                disabled: controlsLocked,
              }
        }
      >
        {isScope ? (
          <div className="flex flex-col gap-2">
            {grouped
              ? REVIEW_PREFLIGHT_GROUPS.map((group) => {
                  const rows = categories.filter(
                    (category) => category.group === group,
                  );
                  if (rows.length === 0) return null;
                  const heading = t(`reviewPreflight.groups.${group}`);
                  return (
                    <section
                      key={group}
                      data-testid={`${testIdPrefix}-group-${group}`}
                      aria-label={heading}
                      className="flex flex-col gap-2"
                    >
                      <h4 className="m-0 text-[13px] font-semibold text-puntt-neutral-gray-12">
                        {heading}
                      </h4>
                      {rows.map((category) => renderRow(category))}
                    </section>
                  );
                })
              : categories.map((category) => renderRow(category))}
            <label
              data-testid={`${testIdPrefix}-scope-other`}
              className="flex cursor-pointer items-start gap-2"
            >
              <Checkbox
                checked={otherChecked}
                disabled={controlsLocked}
                onCheckedChange={() => {
                  if (!controlsLocked) onToggleOther?.();
                }}
                aria-label={t("reviewPreflight.other")}
                size="2"
              />
              <Text size="2" className="text-puntt-neutral-gray-12">
                {t("reviewPreflight.other")}
              </Text>
            </label>
            {otherChecked ? (
              <div className="flex flex-col gap-2 pl-6">
                <Text size="1" className="text-puntt-neutral-gray-11">
                  {t("reviewPreflight.focusLabel")}
                </Text>
                <input
                  data-testid={`${testIdPrefix}-focus`}
                  aria-label={t("reviewPreflight.focusLabel")}
                  placeholder={t("reviewPreflight.focusPlaceholder")}
                  value={focusValue ?? ""}
                  disabled={controlsLocked}
                  onChange={(e) => onFocusChange?.(e.target.value)}
                  className="h-9 w-full rounded-lg border border-puntt-neutral-gray-4 bg-base-white px-2 text-[14px] text-puntt-neutral-gray-12 outline-none focus:border-puntt-accent-8 disabled:cursor-not-allowed disabled:opacity-60"
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </QuestionFormCard>
    </div>
  );
}
